import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabaseBrowser } from "@universe/supabase/browser";
import styles from "./AuthModal.module.scss";
import NavButton from "../SiteNav/NavButton/NavButton";
import type { AuthEvent } from "../../contexts/AuthContext";
import { useAuth } from "../../contexts/AuthContext";
import { useLocale, useLocaleRouteMatch } from "../../i18n/LocaleContext";

type AuthView = "login" | "signup" | "forgotPassword" | "resetPassword" | "verifyEmail";

interface AuthModalProps {
  show: boolean;
  closeAuthModal: () => void;
  authEvent: AuthEvent;
  clearAuthEvent: () => void;
}

export function AuthModal({ show, closeAuthModal, authEvent, clearAuthEvent }: AuthModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, localePath } = useLocale();
  const routeMatches = useLocaleRouteMatch();

  const getPostLoginRedirect = () => {
    const isOnShop = routeMatches("/shop", { end: false });
    return isOnShop ? window.location.pathname : localePath("/account");
  };
  const [view, setView] = useState<AuthView>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const supabase = getSupabaseBrowser();

  // Handle auth events from Supabase
  useEffect(() => {
    if (authEvent === "PASSWORD_RECOVERY") {
      setView("resetPassword");
      setError("");
      setSuccess("");
    }
  }, [authEvent]);

  // Check if user needs to verify email
  useEffect(() => {
    if (user && !user.email_confirmed_at) {
      setEmail(user.email ?? "");
      setView("verifyEmail");
    }
  }, [user?.id, user?.email_confirmed_at]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setName("");
      setPassword("");
      setConfirmPassword("");
      setError("");
      setSuccess("");
      setIsLoading(false);

      // Determine initial view based on state
      if (authEvent === "PASSWORD_RECOVERY") {
        setView("resetPassword");
      } else if (user) {
        if (!user.email_confirmed_at) {
          setEmail(user.email ?? "");
          setView("verifyEmail");
        } else {
          setView("login");
        }
      } else {
        setView("login");
      }
    }
  }, [show, user?.id, authEvent]);

  const isLogin = view === "login";
  const isForgotPassword = view === "forgotPassword";
  const isResetPassword = view === "resetPassword";
  const isVerifyEmail = view === "verifyEmail";
  const verifyEmailInstructions = email
    ? t("auth.verifyEmailInstructionsWithEmail", { email })
    : t("auth.verifyEmailInstructionsNoEmail");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if ((view === "signup" || view === "resetPassword") && password.length < 8) {
      setError(t("auth.errorPasswordMinLength"));
      return;
    }

    if (view === "resetPassword" && password !== confirmPassword) {
      setError(t("auth.errorPasswordMismatch"));
      return;
    }

    setIsLoading(true);

    try {
      if (isResetPassword) {
        // User is authenticated via the recovery link, just update password
        const { error: updateError } = await supabase.auth.updateUser({
          password,
        });

        if (updateError) {
          setError(updateError.message || t("auth.errorResetFailed"));
        } else {
          clearAuthEvent();
          setPassword("");
          setConfirmPassword("");
          setSuccess(t("auth.successPasswordReset"));
          setView("login");
        }
      } else if (isForgotPassword) {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}${localePath("/")}`,
        });

        if (resetError) {
          setError(resetError.message || t("auth.errorResetEmailFailed"));
        } else {
          setSuccess(t("auth.successResetEmailSent"));
        }
      } else if (isLogin) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          setError(signInError.message || t("auth.errorSignInFailed"));
        } else {
          navigate(getPostLoginRedirect());
          closeAuthModal();
        }
      } else if (isVerifyEmail) {
        const { error: resendError } = await supabase.auth.resend({
          type: "signup",
          email,
        });

        if (resendError) {
          setError(resendError.message || t("auth.errorVerifyEmailFailed"));
        } else {
          setSuccess(t("auth.successVerifyEmailSent"));
        }
      } else {
        // Sign up
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { name },
            emailRedirectTo: `${window.location.origin}${getPostLoginRedirect()}`,
          },
        });

        if (signUpError) {
          setError(signUpError.message || t("auth.errorCreateAccountFailed"));
        } else {
          setView("verifyEmail");
          setSuccess(t("auth.successAccountCreated"));
        }
      }
    } catch {
      setError(t("auth.errorUnexpected"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}${getPostLoginRedirect()}`,
        },
      });

      if (oauthError) {
        setError(t("auth.errorGoogleSignIn"));
      }
    } catch {
      setError(t("auth.errorGoogleSignIn"));
    }
  };

  const classes = [styles.AuthModal];
  if (show) {
    classes.push(styles.Show);
  }

  return (
    <>
      <div className={classes.join(" ")}>
        <div className={styles.Container}>
          <h1 className={styles.Title}>
            {isVerifyEmail
              ? t("auth.verifyEmailTitle")
              : isResetPassword
                ? t("auth.setPasswordTitle")
                : isForgotPassword
                  ? t("auth.resetPasswordTitle")
                  : isLogin
                    ? t("auth.signInTitle")
                    : t("auth.createAccountTitle")}
          </h1>
          <p className={styles.Subtitle}>
            {isVerifyEmail
              ? t("auth.verifyEmailSubtitle")
              : isResetPassword
                ? t("auth.setPasswordSubtitle")
                : isForgotPassword
                  ? t("auth.resetPasswordSubtitle")
                  : isLogin
                    ? t("auth.signInSubtitle")
                    : t("auth.createAccountSubtitle")}
          </p>

          {error && <div className={styles.Error}>{error}</div>}
          {success && <div className={styles.Success}>{success}</div>}

          {isVerifyEmail ? (
            <div className={styles.Form}>
              <p className={styles.VerifyEmailText}>{verifyEmailInstructions}</p>
              <button
                type="button"
                onClick={handleSubmit}
                className={styles.SubmitButton}
                disabled={isLoading}
              >
                {isLoading ? t("auth.resending") : t("auth.resendVerification")}
              </button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className={styles.Form}>
            {view === "signup" && (
              <div className={styles.Field}>
                <label htmlFor="auth-name" className={styles.Label}>
                  {t("auth.name")}
                </label>
                <input
                  id="auth-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={styles.Input}
                  placeholder={t("auth.namePlaceholder")}
                  required
                />
              </div>
            )}

            {!isResetPassword && (
              <div className={styles.Field}>
                <label htmlFor="auth-email" className={styles.Label}>
                  {t("auth.email")}
                </label>
                <input
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.Input}
                  placeholder={t("auth.emailPlaceholder")}
                  required
                />
              </div>
            )}

            {!isForgotPassword && (
              <div className={styles.Field}>
                <label htmlFor="auth-password" className={styles.Label}>
                  {isResetPassword ? t("auth.newPassword") : t("auth.password")}
                </label>
                <input
                  id="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={styles.Input}
                  placeholder={isLogin ? t("auth.passwordPlaceholder") : t("auth.passwordHint")}
                  required
                  minLength={isLogin ? undefined : 8}
                />
              </div>
            )}

            {isResetPassword && (
              <div className={styles.Field}>
                <label htmlFor="auth-confirm-password" className={styles.Label}>
                  {t("auth.confirmPassword")}
                </label>
                <input
                  id="auth-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.Input}
                  placeholder={t("auth.confirmPasswordPlaceholder")}
                  required
                  minLength={8}
                />
              </div>
            )}

            {isLogin && !isForgotPassword && (
              <button
                type="button"
                onClick={() => {
                  setView("forgotPassword");
                  setError("");
                  setSuccess("");
                }}
                className={styles.ForgotPasswordButton}
              >
                {t("auth.forgotPassword")}
              </button>
            )}

            <button
              type="submit"
              className={styles.SubmitButton}
              disabled={isLoading}
            >
              {isLoading
                ? isResetPassword
                  ? t("auth.settingPassword")
                  : isForgotPassword
                    ? t("auth.sendingResetLink")
                    : isLogin
                      ? t("auth.signingIn")
                      : t("auth.creatingAccount")
                : isResetPassword
                  ? t("auth.setPassword")
                  : isForgotPassword
                    ? t("auth.sendResetLink")
                    : isLogin
                      ? t("auth.signIn")
                      : t("auth.createAccount")}
            </button>
          </form>
          )}

          {!isForgotPassword && !isResetPassword && !isVerifyEmail && (
            <>
              <div className={styles.Divider}>
                <span>{t("auth.or")}</span>
              </div>

              <button
                onClick={handleGoogleSignIn}
                className={styles.GoogleButton}
                type="button"
              >
                <svg className={styles.GoogleIcon} viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t("auth.continueWithGoogle")}
              </button>
            </>
          )}

          <p className={styles.SwitchLink}>
              {isVerifyEmail ? (
                <>
                  {t("auth.alreadyVerified")} 
                  <button
                    type="button"
                    onClick={() => {
                      setView("login");
                      setError("");
                      setSuccess("");
                    }}
                    className={styles.SwitchButton}
                  >
                    {t("auth.signIn")}
                  </button>
                </>
              ) : isResetPassword ? (
                <>
                  {t("auth.rememberPassword")} 
                  <button
                    type="button"
                    onClick={() => {
                      setView("login");
                      setError("");
                      setSuccess("");
                      clearAuthEvent();
                    }}
                    className={styles.SwitchButton}
                  >
                    {t("auth.signIn")}
                  </button>
                </>
              ) : isForgotPassword ? (
                <>
                  {t("auth.rememberPassword")} 
                  <button
                    type="button"
                    onClick={() => {
                      setView("login");
                      setError("");
                      setSuccess("");
                    }}
                    className={styles.SwitchButton}
                  >
                    {t("auth.signIn")}
                  </button>
                </>
              ) : isLogin ? (
                <>
                  {t("auth.noAccount")} 
                  <button
                    type="button"
                    onClick={() => {
                      setView("signup");
                      setError("");
                    }}
                    className={styles.SwitchButton}
                  >
                    {t("auth.signUp")}
                  </button>
                </>
              ) : (
                <>
                  {t("auth.haveAccount")} 
                  <button
                    type="button"
                    onClick={() => {
                      setView("login");
                      setError("");
                    }}
                    className={styles.SwitchButton}
                  >
                    {t("auth.signIn")}
                  </button>
                </>
              )}
          </p>
        </div>
        <div className={styles.NavButtonContainer}>
          <NavButton
            type="menu"
            collapse={true}
            buttonClickListener={() => closeAuthModal()}
            sideDrawerOpen={true}
          />
        </div>
      </div>
      <div className={styles.Backdrop} onClick={closeAuthModal} />
    </>
  );
}

export default AuthModal;
